import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import '../../styles/Customers.css';
import CustomerProfile from '../Customer/CustomerProfile';
import WeeklySummaryView from '../Client/WeeklySummaryView'; 

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [errorCustomers, setErrorCustomers] = useState(null);
    const [customerNotes, setCustomerNotes] = useState('');
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [errorNotes, setErrorNotes] = useState(null);
    const [showProfile, setShowProfile] = useState(true);
    const [showSummary, setShowSummary] = useState(false); 

    // Plan management states
    const [planStartDate, setPlanStartDate] = useState('');
    const [planEndDate, setPlanEndDate] = useState('');
    const [planStatus, setPlanStatus] = useState('');
    const [loadingPlanActions, setLoadingPlanActions] = useState(false);
    const [planActionError, setPlanActionError] = useState(null);

    // ✅ Fetch customers and their names from profiles
    useEffect(() => {
        const fetchCustomers = async () => {
            setLoadingCustomers(true);
            setErrorCustomers(null);
            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('role', '==', 'customer'));
                const querySnapshot = await getDocs(q);
                const customerList = [];

                for (const userDoc of querySnapshot.docs) {
                    const customerData = userDoc.data();
                    const uid = userDoc.id;
                    let displayName = customerData.email || 'Unknown';

                    try {
                        const profileRef = doc(db, 'profiles', uid);
                        const profileSnap = await getDoc(profileRef);
                        if (profileSnap.exists()) {
                            const profileData = profileSnap.data();
                            if (profileData.name && profileData.name.trim() !== '') {
                                displayName = profileData.name.trim();
                            }
                        }
                    } catch (profileErr) {
                        console.warn(`Failed to fetch profile for ${uid}:`, profileErr);
                    }

                    customerList.push({
                        id: uid,
                        ...customerData,
                        displayName,
                        notes: customerData.notes || '',
                        planStartDate: customerData.planStartDate || '',
                        planEndDate: customerData.planEndDate || '',
                        planStatus: customerData.planStatus || 'not_set'
                    });
                }

                setCustomers(customerList);
            } catch (error) {
                console.error('Error fetching customers:', error);
                setErrorCustomers('Failed to fetch customers.');
            } finally {
                setLoadingCustomers(false);
            }
        };

        fetchCustomers();
    }, []);

    // Update notes and plan when customer changes
    useEffect(() => {
        if (selectedCustomer) {
            setCustomerNotes(selectedCustomer.notes || '');
            setPlanStartDate(selectedCustomer.planStartDate || '');
            setPlanEndDate(selectedCustomer.planEndDate || '');
            setPlanStatus(selectedCustomer.planStatus || 'not_set');
        } else {
            setCustomerNotes('');
            setPlanStartDate('');
            setPlanEndDate('');
            setPlanStatus('');
        }
        setPlanActionError(null);
    }, [selectedCustomer]);

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        setShowProfile(true);
        setShowSummary(false); 
    };

    const handleNotesChange = (e) => setCustomerNotes(e.target.value);

    const saveCustomerNotes = async () => {
        if (!selectedCustomer) return;
        setLoadingNotes(true);
        setErrorNotes(null);
        try {
            const userDocRef = doc(db, 'users', selectedCustomer.id);
            await updateDoc(userDocRef, { notes: customerNotes });

            setCustomers(prev =>
                prev.map(c => (c.id === selectedCustomer.id ? { ...c, notes: customerNotes } : c))
            );
            setSelectedCustomer(prev => ({ ...prev, notes: customerNotes }));
        } catch (error) {
            console.error('Error saving notes:', error);
            setErrorNotes('Failed to save notes.');
        } finally {
            setLoadingNotes(false);
        }
    };

    // Plan management functions (remain the same)
    const updateCustomerPlan = async (updates) => {
        if (!selectedCustomer) {
            setPlanActionError("No customer selected.");
            return false;
        }
        setLoadingPlanActions(true);
        setPlanActionError(null);
        try {
            const userDocRef = doc(db, 'users', selectedCustomer.id);
            await updateDoc(userDocRef, updates);

            setCustomers(prev =>
                prev.map(c => (c.id === selectedCustomer.id ? { ...c, ...updates } : c))
            );
            setSelectedCustomer(prev => ({ ...prev, ...updates }));
            setLoadingPlanActions(false);
            return true;
        } catch (error) {
            console.error('Error updating plan:', error);
            setPlanActionError(`Failed to update plan: ${error.message}`);
            setLoadingPlanActions(false);
            return false;
        }
    };

    const handleUpdatePlanDates = async () => {
        if (!planStartDate || !planEndDate) {
            setPlanActionError("Please select both start and end dates.");
            return;
        }
        if (new Date(planStartDate) > new Date(planEndDate)) {
            setPlanActionError("Start date cannot be after end date.");
            return;
        }
        await updateCustomerPlan({ planStartDate, planEndDate, planStatus: 'active' });
    };

    const handlePausePlan = async () => await updateCustomerPlan({ planStatus: 'paused' });
    
    const handleResumePlan = async () => {
        // Ensure dates are set and end date is not in the past before resuming.
        if (!planStartDate || !planEndDate || new Date(planEndDate) < new Date()) {
            setPlanActionError("Please set valid start and end dates before resuming.");
            return;
        }
        await updateCustomerPlan({ planStatus: 'active' });
    };
    
    const handleRenewPlan = async () => {
        if (!planStartDate || !planEndDate) {
            setPlanActionError("Please set new start and end dates.");
            return;
        }
        if (new Date(planStartDate) > new Date(planEndDate)) {
            setPlanActionError("Start date cannot be after end date.");
            return;
        }
        await updateCustomerPlan({ planStartDate, planEndDate, planStatus: 'active' });
    };

    // Toggles updated to hide the others
    const toggleProfile = () => {
        setShowProfile(!showProfile);
        setShowSummary(false);
    };

    // NEW TOGGLE FUNCTION
    const toggleSummary = () => {
        setShowSummary(!showSummary);
        setShowProfile(false);
    };

    if (loadingCustomers) return <div className="customers-container">Loading customers...</div>;
    if (errorCustomers) return <div className="customers-container">Error: {errorCustomers}</div>;

    return (
        <div className="customers-container">
            <aside className="customer-list-sidebar">
                <h3>Customers</h3>
                <ul>
                    {customers.map(customer => (
                        <li
                            key={customer.id}
                            className={selectedCustomer?.id === customer.id ? 'selected' : ''}
                            onClick={() => handleCustomerSelect(customer)}
                        >
                            {customer.displayName}
                        </li>
                    ))}
                </ul>
            </aside>

            <main className="customer-details">
                {selectedCustomer ? (
                    <>
                        <h2>{selectedCustomer.displayName}</h2>
                        <div className="customer-toggles">
                            <button
                                type="button"
                                className={showProfile ? 'active' : ''}
                                onClick={toggleProfile}
                            >
                                {showProfile ? 'Hide Profile' : 'Show Profile'}
                            </button>
                            <button
                                type="button"
                                className={showSummary ? 'active' : ''}
                                onClick={toggleSummary}
                            >
                                {showSummary ? 'Hide Weekly Summary' : 'Show Weekly Summary'}
                            </button>
                        </div>

                        {showProfile && <CustomerProfile customerId={selectedCustomer.id} />}

                        {showSummary && (
                            <div className="weekly-summary-section">
                                <WeeklySummaryView customerId={selectedCustomer.id} />
                            </div>
                        )}

                        <div className="customer-plan-management-section">
                            <h3>Plan Management</h3>
                            <p><strong>Status:</strong> {planStatus}</p>
                            <p><strong>Start:</strong> {planStartDate || 'Not Set'}</p>
                            <p><strong>End:</strong> {planEndDate || 'Not Set'}</p>

                            <div className="plan-date-inputs">
                                <label htmlFor="planStartDate">Start Date:</label>
                                <input type="date" id="planStartDate" value={planStartDate} onChange={e => setPlanStartDate(e.target.value)} disabled={loadingPlanActions} />
                                <label htmlFor="planEndDate">End Date:</label>
                                <input type="date" id="planEndDate" value={planEndDate} onChange={e => setPlanEndDate(e.target.value)} disabled={loadingPlanActions} />
                                <button onClick={handleUpdatePlanDates} disabled={loadingPlanActions}>
                                    {loadingPlanActions ? 'Updating...' : 'Set/Update Dates'}
                                </button>
                            </div>

                            <div className="plan-action-buttons">
                                {planStatus === 'active' ? (
                                    <button onClick={handlePausePlan} disabled={loadingPlanActions}>
                                        {loadingPlanActions ? 'Pausing...' : 'Pause Plan'}
                                    </button>
                                ) : (
                                    <button onClick={handleResumePlan} disabled={loadingPlanActions}>
                                        {loadingPlanActions ? 'Resuming...' : 'Resume Plan'}
                                    </button>
                                )}
                                <button onClick={handleRenewPlan} disabled={loadingPlanActions}>
                                    {loadingPlanActions ? 'Renewing...' : 'Renew Plan'}
                                </button>
                            </div>
                            {planActionError && <p className="error-message">{planActionError}</p>}
                        </div>

                        <div className="customer-notes-section">
                            <h3>Notes for {selectedCustomer.displayName}</h3>
                            <textarea
                                value={customerNotes}
                                onChange={handleNotesChange}
                                placeholder="Add notes about this customer..."
                            />
                            <button onClick={saveCustomerNotes} disabled={loadingNotes}>
                                {loadingNotes ? 'Saving Notes...' : 'Save Notes'}
                            </button>
                            {errorNotes && <p className="error-message">{errorNotes}</p>}
                        </div>
                    </>
                ) : (
                    <p>Select a customer from the sidebar to view their details.</p>
                )}
            </main>
        </div>
    );
};

export default Customers;